/**
 * useFocusTrap — Phase 6 a11y hook.
 *
 * WCAG 2.1 AA §2.4.3 (Focus Order) + §2.1.2 (No Keyboard Trap, inverted:
 * modals MUST retain focus until dismissed).
 *
 * Usage inside a modal / dialog / dismissable panel:
 *
 *   const panelRef = useRef(null);
 *   useFocusTrap(panelRef, isOpen, {
 *     onEscape: () => setOpen(false),
 *     initialFocus: () => firstButtonRef.current,  // optional
 *   });
 *
 *   return isOpen ? (
 *     <div role="dialog" aria-modal="true" ref={panelRef} tabIndex={-1}>
 *       ...
 *     </div>
 *   ) : null;
 *
 * Behavior when `isOpen` is truthy:
 *   1. Saves `document.activeElement` (the trigger button).
 *   2. Moves focus to the first focusable descendant of `panelRef.current`
 *      (or to the panel itself if none exists).
 *   3. Traps Tab / Shift+Tab within the panel — wraps end-of-list back to
 *      start (Tab) or start-to-end (Shift+Tab).
 *   4. On Escape, calls `onEscape` (if provided).
 *   5. On unmount (or `isOpen` flips to falsy), restores focus to the
 *      previously-saved element.
 *
 * Focusable-element enumeration matches the WHATWG heuristic:
 *   button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])
 * — excluding disabled elements.
 */
import { useEffect } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(containerRef, isOpen, options = {}) {
  const { onEscape, initialFocus, returnFocusRef } = options;

  useEffect(() => {
    if (!isOpen) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    // Save the element that had focus when the modal opened so we can
    // restore it on close. Read synchronously — `useEffect` runs after
    // render, but before `requestAnimationFrame` moves focus into the
    // panel, so the currently-focused element is still the trigger IF
    // the trigger was focused when it activated (keyboard Space/Enter,
    // or real mouse mousedown-focus). If the caller passed
    // `returnFocusRef`, that takes precedence over auto-capture — this
    // is the escape hatch when the trigger was activated
    // programmatically (dispatchEvent) or when the user is on Safari
    // (buttons don't receive focus on click there).
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Snapshot the returnFocusRef at effect-entry so the react-hooks
    // exhaustive-deps lint stays happy AND we capture the ref target
    // BEFORE the modal renders any content that could inadvertently
    // reshape the ref (e.g., a nested Modal that shares a ref).
    const explicitReturnTarget =
      returnFocusRef && returnFocusRef.current instanceof HTMLElement
        ? returnFocusRef.current
        : null;

    // Enumerate focusable descendants each time — DOM can mutate during
    // the modal's lifetime (async body content, tab switches, etc.).
    const getFocusables = () => {
      const nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
      return Array.from(nodes).filter(
        (n) => n instanceof HTMLElement && !n.hasAttribute("aria-hidden"),
      );
    };

    // Move initial focus. Prefer `options.initialFocus`, then the first
    // focusable descendant, then the container itself.
    const raf = requestAnimationFrame(() => {
      let target = null;
      if (typeof initialFocus === "function") {
        target = initialFocus();
      }
      if (!target) {
        const focusables = getFocusables();
        target = focusables[0] || container;
      }
      if (target && typeof target.focus === "function") {
        target.focus();
      }
    });

    // Trap Tab / Shift+Tab within the container. Escape delegates to
    // consumer.
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (typeof onEscape === "function") {
          e.stopPropagation();
          onEscape(e);
        }
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = getFocusables();
      if (focusables.length === 0) {
        // Nothing focusable inside — pin focus on the panel.
        e.preventDefault();
        container.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        // Shift+Tab on first → wrap to last.
        if (active === first || !container.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab on last → wrap to first.
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKey);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", handleKey);
      // Restore focus — priority order:
      //   1. `options.returnFocusRef.current` (snapshotted at effect
      //      entry as `explicitReturnTarget`) if provided and in DOM
      //      (explicit consumer intent — always wins).
      //   2. `previouslyFocused` if it's a "real" element (not `<body>`
      //      and still in the DOM).
      //   3. No-op — leave focus wherever the browser puts it (body).
      //
      // The explicit-ref path fixes the case where the trigger was
      // activated programmatically (`dispatchEvent` / test harness) or
      // in a browser that doesn't focus buttons on mouse click (Safari).
      // In both cases `document.activeElement` at effect-time was
      // `<body>`, which would otherwise silently break return-focus.
      if (
        explicitReturnTarget &&
        typeof explicitReturnTarget.focus === "function" &&
        document.contains(explicitReturnTarget)
      ) {
        explicitReturnTarget.focus();
        return;
      }

      if (
        previouslyFocused &&
        previouslyFocused !== document.body &&
        typeof previouslyFocused.focus === "function" &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
}

export default useFocusTrap;
